/**
 * Invoice PDF contract — the request/response shapes for generating a
 * customer-facing invoice PDF and storing it in GCS.
 *
 * Lives in `shared` (not in the api app) because it's the wire contract:
 * the api service consumes it today, and the typed client in
 * `@clockwise/clients` plus any agent hitting the endpoint consume the same
 * definitions tomorrow (G14 — typed clients are the contract).
 *
 * Deliberately NOT a persisted domain: there is no `invoices` table behind
 * this. The caller owns invoice numbering, line-item derivation, and any
 * accounting record; this contract describes a *document to render*. Keeping
 * it presentation-shaped (address lines, not an address entity) is what lets
 * the same renderer serve time-and-materials invoices, cross-charge invoices,
 * and retainer invoices without the PDF layer knowing about any of them.
 *
 * Money rules, in one place so they can't drift:
 *   - every monetary value is a two-decimal STRING, in the invoice's
 *     `currency`. Strings all the way through — the `numeric(12,2)` columns,
 *     the API envelope, the typed clients and form state are already strings,
 *     and this document used to be the one float island between them. See
 *     `./money.ts` for the arithmetic and why it is decimal, not cents.
 *   - the caller never supplies `total`, `paymentsCredits` or `balanceDue`.
 *     Those are computed from the line items by {@link computeInvoiceTotals}
 *     so a PDF can never show a total that doesn't add up to the rows
 *     printed above it.
 */

import { z } from 'zod';
import { isoDateString } from './iso-date';
import {
  compareMoney,
  hasMoneyScale,
  isMoney,
  multiplyMoney,
  subtractMoney,
  sumMoney,
  toMoney,
} from './money';

/** Hard cap on line items in one invoice. Generous — the sample invoices run
 *  3–10 rows — but bounded so a runaway caller can't ask for a 10k-page PDF. */
export const MAX_INVOICE_LINE_ITEMS = 500;

/** Cap on a single line-item description. Long descriptions are expected (the
 *  reference invoices carry a full paragraph per role) and the renderer wraps
 *  and page-splits them, so this only exists to bound the work. */
export const MAX_INVOICE_DESCRIPTION_CHARS = 4000;

/**
 * A monetary amount, as a two-decimal string.
 *
 * Validated rather than parsed-to-number: `numeric(12,2)` is the storage, and
 * a value carrying a third decimal is REJECTED rather than rounded, so the
 * figure an operator typed and the figure a customer is charged can never
 * differ by a silent Postgres round.
 *
 * Negative values are allowed — credit lines and write-offs are real invoice
 * rows — but the total is not allowed to be nonsensical; see
 * {@link computeInvoiceTotals}.
 */
const money = (label: string) =>
  z
    .string()
    .trim()
    .refine((v) => isMoney(v), `${label} must be a decimal amount`)
    .refine((v) => hasMoneyScale(v), `${label} must have at most 2 decimal places`)
    // Canonicalized on parse, so '5' and '5.00' produce the same document and
    // nothing downstream has to normalize again.
    .transform((v) => toMoney(v, label));

/** A quantity (hours, units). Two decimals, matching `numeric(6,2)` hours
 *  storage elsewhere in the codebase. Non-negative: a negative quantity with
 *  a positive rate is almost always a caller bug — express credits as a
 *  negative `amount` instead. */
const quantity = z
  .string()
  .trim()
  .refine((v) => isMoney(v), 'quantity must be a decimal number')
  .refine((v) => compareMoney(v, '0') >= 0, 'quantity cannot be negative')
  .refine((v) => hasMoneyScale(v), 'quantity must have at most 2 decimal places')
  .transform((v) => toMoney(v, 'quantity'));

/** ISO-4217 alphabetic currency code. Uppercased on parse so `'usd'` and
 *  `'USD'` produce the same document. */
export const invoiceCurrencySchema = z
  .string()
  .trim()
  .length(3, 'currency must be a 3-letter ISO-4217 code')
  .regex(/^[A-Za-z]{3}$/, 'currency must be a 3-letter ISO-4217 code')
  .transform((s) => s.toUpperCase())
  .default('USD');

const nonEmptyLine = (max: number, label: string) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} is too long`);

/**
 * A value that must render as exactly ONE line, whatever it arrives as.
 *
 * `nonEmptyLine` only trims the ends, so an embedded newline survives it, and
 * a party NAME is drawn standalone at a fixed coordinate with no line
 * accounting behind it — a second line would simply land on the address block
 * below. Collapsing is the right answer here because a name has no meaningful
 * line structure to preserve.
 *
 * Deliberately NOT applied to two neighbours that do:
 *   - `addressLines`, where a newline means a genuine line break; the renderer
 *     splits it into real lines and shrink-to-fit absorbs them.
 *   - line-item descriptions, where `paginate.ts` preserves newlines when it
 *     splits a description across pages.
 */
const singleLine = (max: number, label: string) =>
  z
    .string()
    .transform((s) => s.replace(/\s+/g, ' ').trim())
    .pipe(nonEmptyLine(max, label));

/**
 * The "from" block printed top-left on every page: who is billing.
 *
 * Address is a list of pre-formatted lines rather than structured
 * street/city/postal fields on purpose — the issuer block is typography, not
 * data. Callers that DO hold structured addresses format them once at the
 * boundary; the renderer stays out of international address ordering.
 */
export const invoicePartySchema = z.object({
  name: singleLine(120, 'name'),
  /**
   * Street/city/country, already ordered for display.
   *
   * The cap is a layout constraint, like `paymentDetails`: the issuer block
   * and the Bill To panel are fixed slots in the invoice grid. Extra lines
   * shrink the block to fit rather than spilling over the panel below, and 6
   * is the point past which that shrinking stops being legible.
   */
  addressLines: z
    .array(nonEmptyLine(120, 'address line'))
    .max(6, 'at most 6 address lines')
    .default([]),
  phone: z.string().trim().max(40).optional(),
  fax: z.string().trim().max(40).optional(),
  email: z.string().trim().max(160).optional(),
  website: z.string().trim().max(160).optional(),
});

export type InvoiceParty = z.infer<typeof invoicePartySchema>;

/**
 * One row of the items table.
 *
 * `amount` is optional: when omitted it's derived as `quantity × rate`
 * (rounded to 2dp). When supplied it wins, which is what makes flat-fee rows
 * ("QBO. QuickBooks Online Plus"), discounts, and rounding adjustments
 * expressible. The refine below forbids the one ambiguous combination — no
 * amount AND no way to compute one.
 */
export const invoiceLineItemSchema = z
  .object({
    description: nonEmptyLine(MAX_INVOICE_DESCRIPTION_CHARS, 'description'),
    quantity: quantity.optional(),
    rate: money('rate').optional(),
    amount: money('amount').optional(),
  })
  .refine((li) => li.amount !== undefined || (li.quantity !== undefined && li.rate !== undefined), {
    message: 'provide amount, or both quantity and rate so it can be computed',
    path: ['amount'],
  });

export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;

/**
 * The full document. One schema, rendered to one PDF.
 *
 * Every field that appears in a labelled box on the reference invoice is
 * optional except the ones a document is meaningless without (issuer, bill-to,
 * number, date, at least one line). An absent optional field renders as an
 * empty box — the box itself always prints, because the reference layout is a
 * fixed grid and collapsing it would produce a subtly different-looking
 * invoice depending on data.
 */
export const invoiceDocumentSchema = z.object({
  issuer: invoicePartySchema,
  billTo: invoicePartySchema,

  /** Human-facing invoice number, e.g. `'26-07-750'`. Also the default
   *  filename stem, so it's constrained to something path-safe-able. */
  invoiceNumber: nonEmptyLine(64, 'invoiceNumber'),
  /** Invoice ("Date") — `YYYY-MM-DD`. */
  invoiceDate: isoDateString('invoiceDate must be a valid date in YYYY-MM-DD format'),
  /** `YYYY-MM-DD`. Optional: some terms have no fixed due date. */
  dueDate: isoDateString('dueDate must be a valid date in YYYY-MM-DD format').optional(),

  /** Free text, e.g. `'Net 30'`. Not an enum — terms are negotiated per
   *  customer and this is a printed label, not a computed schedule. */
  terms: z.string().trim().max(60).optional(),
  /** Free text, e.g. `'June 2026'`. Printed next to "Service for the period".
   *  Derived from the invoice's stored date range by its caller — there is no
   *  hand-typed label behind it. */
  servicePeriod: z.string().trim().max(80).optional(),

  currency: invoiceCurrencySchema,

  lineItems: z
    .array(invoiceLineItemSchema)
    .min(1, 'an invoice needs at least one line item')
    .max(MAX_INVOICE_LINE_ITEMS, `at most ${MAX_INVOICE_LINE_ITEMS} line items`),

  /**
   * Remittance instructions, printed in the bottom-left box on the final
   * page. Pre-formatted lines for the same reason as `addressLines`.
   *
   * The caps are a layout constraint, not an arbitrary limit: that box is a
   * fixed slot in the invoice grid, and 12 lines of ≤120 characters is what
   * fits at the panel's normal type size. Longer content still renders — the
   * renderer steps the font down — but past these bounds it would have to
   * truncate.
   */
  paymentDetails: z
    .array(z.string().trim().max(120, 'payment-detail line is too long'))
    .max(12, 'at most 12 payment-detail lines')
    .default([]),
});

export type InvoiceDocumentInput = z.input<typeof invoiceDocumentSchema>;
/** Parsed/normalized document — defaults applied, currency uppercased. */
export type InvoiceDocument = z.infer<typeof invoiceDocumentSchema>;

/** Caps on `storage.metadata`, chosen against the ~8KiB GCS limit for custom
 *  object metadata (20 × (64 + 256) ≈ 6.4KB, comfortably under). Bounded here
 *  so an oversized map fails as a `VALIDATION_ERROR` with a field path, rather
 *  than being rendered, uploaded, and rejected by GCS as a 400 that surfaces
 *  to the caller as a retryable-looking 502. */
export const MAX_INVOICE_METADATA_ENTRIES = 20;

/**
 * Caller-controllable parts of where the rendered PDF lands.
 *
 * Deliberately does NOT expose the bucket or a full object key. The derived
 * key is `{prefix}/{tenantId}/{YYYY-MM}/{number}.pdf`, and that `tenantId`
 * segment is the tenant isolation boundary inside the bucket — a request that
 * could name an arbitrary key could read or overwrite another tenant's
 * invoice. `keyPrefix` is safe because it only ever *precedes* the tenant
 * segment and is slugified per path segment, so it cannot climb out.
 *
 * Trusted internal callers that genuinely need a different destination pass
 * `InvoicePdfStorageOverrides` to the service directly — a separate argument
 * that no request body can reach.
 */
export const invoiceStorageOptionsSchema = z.object({
  /** Prefix for the derived key. Slugified per segment; the tenant segment is
   *  always appended after it. */
  keyPrefix: z.string().trim().max(512).optional(),
  /** Custom object metadata, merged onto the stored object. The service's own
   *  identifying keys (tenantId, invoiceNumber, …) always win. */
  metadata: z
    .record(
      z.string().trim().min(1, 'metadata key is required').max(64, 'metadata key is too long'),
      z.string().max(256, 'metadata value is too long'),
    )
    .refine(
      (m) => Object.keys(m).length <= MAX_INVOICE_METADATA_ENTRIES,
      `at most ${MAX_INVOICE_METADATA_ENTRIES} metadata entries`,
    )
    .optional(),
});

export type InvoiceStorageOptions = z.infer<typeof invoiceStorageOptionsSchema>;

/**
 * Destination overrides for TRUSTED callers only.
 *
 * Not part of the request schema and never populated from a request body: an
 * arbitrary bucket or key escapes the per-tenant path that isolates one
 * tenant's invoices from another's. Reserved for in-process callers that own
 * their own archive layout (a migration, a back-fill job), which are
 * accountable for the tenant scoping themselves.
 */
export interface InvoicePdfStorageOverrides {
  bucket?: string;
  key?: string;
}

/**
 * REQUEST — what the generate method accepts.
 */
export const generateInvoicePdfRequestSchema = z.object({
  invoice: invoiceDocumentSchema,
  /**
   * The invoice's id (`invoices.id`). Its PDFs file under it —
   * `{prefix}/{tenantId}/{invoiceId}/pdfs/…` — beside its attachments, so a
   * renumbered draft's documents stay in one folder.
   */
  invoiceId: z.string().uuid('invoiceId must be a uuid'),
  storage: invoiceStorageOptionsSchema.optional(),
  /**
   * Replace an object that already exists at the target key.
   *
   * Defaults to `false`, which makes the upload conditional
   * (`ifGenerationMatch: 0`) and surfaces a `CONFLICT` instead of silently
   * overwriting. An invoice PDF is a document a customer may already have
   * received; overwriting it in place must be a deliberate act.
   */
  overwrite: z.boolean().default(false),
});

export type GenerateInvoicePdfRequestInput = z.input<typeof generateInvoicePdfRequestSchema>;
export type GenerateInvoicePdfRequest = z.infer<typeof generateInvoicePdfRequestSchema>;

/** Computed money summary printed in the totals block. */
export interface InvoiceTotals {
  /** Sum of every line item's effective amount. Two-decimal string. */
  total: string;
  /**
   * Payments and credits already applied against this invoice, deducted from
   * {@link InvoiceTotals.total} to reach {@link InvoiceTotals.balanceDue}.
   *
   * Always '0.00' today: nothing on an invoice records a receipt or a credit
   * memo yet. The row prints regardless, because the reference invoice prints
   * it and a customer reading "Total 1,000.00 / Balance Due 1,000.00" with no
   * line between them cannot tell whether their payment was missed or simply
   * never applied. When receipts exist, this is the one field to populate.
   */
  paymentsCredits: string;
  /**
   * What the customer owes: total minus payments and credits. Two-decimal
   * string, and today always equal to {@link InvoiceTotals.total}.
   */
  balanceDue: string;
}

/** Where the PDF ended up. */
export interface InvoicePdfStorageRef {
  bucket: string;
  key: string;
  /** `gs://bucket/key` — the form most GCP tooling wants. */
  gsUri: string;
  /** GCS object generation. Changes on every overwrite; pin it to reference
   *  this exact revision of the object. */
  generation: string;
  sizeBytes: number;
  contentType: string;
  /** Base64 MD5 of the stored bytes, as reported by GCS. Compare against a
   *  locally computed digest to prove the upload wasn't corrupted. */
  md5Hash: string | null;
  /** ISO-8601 instant the object was created, from GCS. */
  uploadedAt: string;
}

/**
 * RESPONSE — what the generate method returns.
 *
 * Carries the rendered document's computed facts (totals, page count)
 * alongside the storage pointer so a caller can record what it sent without
 * re-deriving the arithmetic or re-opening the PDF.
 */
export interface GenerateInvoicePdfResult {
  storage: InvoicePdfStorageRef;
  invoiceNumber: string;
  currency: string;
  totals: InvoiceTotals;
  lineItemCount: number;
  pageCount: number;
}

/** A line's effective amount: explicit `amount` when given, else
 *  `quantity × rate` rounded to cents. Two-decimal string. */
export function lineItemAmount(li: InvoiceLineItem): string {
  // Canonicalized rather than passed through: parsed documents arrive
  // two-decimal already, but this stays callable on hand-built objects and
  // its contract is one shape, not "whatever the caller happened to hold".
  if (li.amount !== undefined) return toMoney(li.amount, 'amount');
  // The schema's refine guarantees both are present when `amount` is absent;
  // the `?? '0'` is unreachable for parsed input and only keeps this callable
  // on hand-built objects.
  return multiplyMoney(li.quantity ?? '0', li.rate ?? '0');
}

/**
 * Payments and credits applied to an invoice, until something tracks them.
 *
 * A named constant rather than a literal in the return so the day receipts
 * land there is exactly one place to look, and the arithmetic below already
 * does the subtraction instead of having to grow it.
 */
const ZERO_PAYMENTS_CREDITS = '0.00';

/**
 * Derive the totals block from the document. Single source of truth — the
 * renderer, the API response and the web preview all call this, so the printed
 * total, the returned total and the one on screen cannot disagree.
 */
export function computeInvoiceTotals(doc: InvoiceDocument): InvoiceTotals {
  const total = sumMoney(doc.lineItems.map(lineItemAmount));
  const paymentsCredits = ZERO_PAYMENTS_CREDITS;
  return { total, paymentsCredits, balanceDue: subtractMoney(total, paymentsCredits) };
}
